import { customGaEvent, GA_EVENTS } from '../../../utils/ga';
import 'components/AFrame/components/aabCollider';

/* eslint-disable no-undef */
if (typeof AFRAME === 'undefined') {
  throw new Error(
    'Primitive attempted to register before AFRAME was available.'
  );
}

AFRAME.registerPrimitive('a-hotspot', {
  defaultComponents: {
    hotspot: {},
  },
  mappings: {
    hotspottitle: 'hotspot.hotspottitle',
    content: 'hotspot.content',
    disabled: 'hotspot.disabled',
    icon: 'hotspot.icon',
    src: 'hotspot.src',
    id: 'hotspot.id',
    translatecontent: 'hotspot.translatecontent',
    translatetitle: 'hotspot.translatetitle',
    underedits: 'hotspot.underedits',
    darkmode: 'hotspot.darkmode',
    url: 'hotspot.url',
    active: 'hotspot.active',
    play: 'hotspot.play',
    type: 'hotspot.type',
    rotateto: 'hotspot.rotateto',
    centerhotspot: 'hotspot.centerhotspot',
    tourid: 'hotspot.tourid',
    tourname: 'hotspot.tourname',
    hotspotname: 'hotspot.hotspotname',
    hidden: 'hotspot.hidden',
    aid: 'hotspot.aid',
    showlabel: 'hotspot.showlabel',
    focusoutlineicon: 'hotspot.focusoutlineicon',
    hotspotid: 'hotspot.hotspotid',
    role: 'hotspot.role',
    tabIndex: 'hotspot.tabIndex',
    'aria-expanded': 'hotspot.ariaexpanded',
    'aria-label': 'hotspot.arialabel',
  },
});

AFRAME.registerComponent('hotspot', {
  schema: {
    active: { type: 'boolean', default: false },
    content: { type: 'string', default: '' },
    darkmode: { type: 'boolean', default: false },
    underedits: { type: 'boolean', default: false },
    disabled: { type: 'boolean', default: false },
    id: { type: 'string', default: '' },
    icon: { type: 'string', default: '' },
    src: { type: 'string', default: '' },
    url: { type: 'string', default: '' },
    play: { type: 'string', default: '' },
    rotateto: { type: 'string', default: '' },
    centerhotspot: { type: 'boolean', default: false },
    hotspottitle: { type: 'string', default: '' },
    translatecontent: { type: 'string', default: '' },
    translatetitle: { type: 'string', default: '' },
    type: { type: 'string', default: '' },
    tourid: { type: 'string', default: '' },
    tourname: { type: 'string', default: '' },
    hotspotname: { type: 'string', default: '' },
    hidden: { type: 'boolean', default: false },
    aid: { type: 'string', default: '' },
    showlabel: { type: 'boolean', default: false },
    focusoutlineicon: { type: 'string', default: '' },
    hotspotid: { type: 'string', default: '' },
    role: { type: 'string', default: 'button' },
    tabIndex: { type: 'number', default: 0 },
    'aria-expanded': { type: 'boolean', default: false },
    'aria-label': { type: 'string', default: '' },
},

  init() {
    this.scene = document.getElementById('scene');
    if (this.data.hotspottitle) {
      this.hotspotTitle = document.querySelector(`#${this.data.hotspottitle}`);
      if (this.hotspotTitle) {
        const { x, y, z } = this.hotspotTitle.getAttribute('position');
        this.hotspotTitlePosition = `${x} ${y} ${z}`;
        this.initializeTitleAnimations();
        this.hotspotTitle.setAttribute('depth-write', false);
      }
    }
    if (this.data.focusoutlineicon) {
      this.focusOutlineIcon = document.querySelector(
        `#${this.data.focusoutlineicon}`
      );
    }

    if (this.data.icon) {
      this.hotspotIcon = document.querySelector(`#${this.data.icon}`);
      if (this.hotspotIcon) {
        this.iconSize = this.hotspotIcon.getAttribute('width');
        this.iconSrc = this.hotspotIcon.getAttribute('src');
        this.setHotspotListeners(true);
        this.setIconAnimations(true);
        this.setCollisionDetection(this.hotspotIcon, true);
      }
    }

    if (this.data.content) {
      const offsetY = this.iconSize / 2 || 0;
      this.hotspotContent = document.querySelector(`#${this.data.content}`);
      if (this.hotspotContent) {
        const { y } = this.hotspotContent.getAttribute('position');
        this.hotspotContentYPosition = y + offsetY;
        this.hotspotContentOpacity = this.hotspotContent.getAttribute(
          'opacity'
        );
        this.setContentAnimations(true);
      }
    }

    if (this.data.type === 'video' && this.data.play) {
      this.playButton = document.querySelector(`#${this.data.play}`);
      this.setThumbnailAnimations(true);
    }

    // set image here to avoid assets flashing on load
    if (this.data.src && this.hotspotContent) {
      this.hotspotContent.setAttribute('src', this.data.src);
    }

    this.isHovered = false;
    this.underedits = this.data.underedits;
    this.disabled = this.data.disabled;
  },

  update(oldData) {
    // update icon normally for other hotspots
    if (oldData.active !== this.data.active && this.data.type === 'highlight') {
      this.updateIcon();
      this.hotspotTitle.setAttribute('visible', this.data.active);
      this.el.setAttribute('aria-expanded', this.data.active);
    }

    if (oldData.darkmode !== this.data.darkmode) {
      this.updateIcon();
    }

    if (oldData.icon !== this.data.icon) {
      this.iconSrc = this.hotspotIcon.getAttribute('src');
    }

    if (oldData.disabled !== this.data.disabled) {
      this.disabled = this.data.disabled;
    }

    if (oldData.underedits !== this.data.underedits) {
      this.underedits = this.data.underedits;
    }

    if (oldData.translatetitle !== this.data.translatetitle) {
      this.removeTitleAnimations();
      this.initializeTitleAnimations();
    }

    if (
      oldData.centerhotspot !== this.data.centerhotspot &&
      this.data.centerhotspot &&
      !this.data.active &&
      !this.data.disabled
    ) {
      this.rotateToHotspot();
    }

    if (!oldData.hidden && this.data.hidden) {
      const entity = this.el;
      entity.parentNode.removeChild(entity);
    }

    this.reset(oldData);
  },

  //---------------------------------------------------------------------------------------
  // event handlers

  handleMouseEnter() {
    if (!this.disabled && this.hotspotTitle) {
      this.hotspotTitle.setAttribute('visible', true);
    }
    this.isHovered = true;
  },

  handleMouseLeave() {
    // TODO: error thrown when leaving VR mode
    if (!this.disabled && this.hotspotTitle && !this.data.active) {
      this.hotspotTitle.setAttribute('visible', false);
    }
    this.isHovered = false;
  },

  handleFocus() {
    this.focusOutlineIcon.setAttribute('visible', true);
    this.scene.emit('hotspot-focus');
    this.rotateToHotspot();
  },

  handleFocusOut() {
    this.focusOutlineIcon.setAttribute('visible', false);
  },

  handleExternalLink(event) {
    if (event.type === "click" || event.key === "Enter") {
      const ref = event.target ?? event.srcElement;
      if (ref) {
        this.fireGoogleAnalyticsEvents(this.data.active);
        this.scene.emit('hotspot-open', {
          hotspotId: this.data.hotspotid,
          type: this.data.type,
        });
        window.open(this.data.url, '_blank', 'noopener,noreferrer');
      }
    }
  },

  handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      if (event.key === ' ') {
        event.preventDefault();
      }

      if (this.data.hotspotid) {
        this.fireGoogleAnalyticsEvents(this.data.active);
        this.scene.emit('hotspot-open', {
          hotspotId: this.data.hotspotid,
          type: this.data.type,
        });
      }
    }
  },

  handleClick(event) {
    // do not close the hotspot if content is clicked
    if (
      this.hotspotContent &&
      event.target &&
      this.hotspotContent.contains(event.target)
    ) {
      return;
    }

    if (!this.disabled) {
      this.fireGoogleAnalyticsEvents(this.data.active);
    }

    if (this.data.hotspotid) {
      this.scene.emit('hotspot-open',
        {
          hotspotId: this.data.hotspotid,
          type: this.data.type,
        });
    }

    // external link hotspots open in a new tab, and this keeps the
    // mouse positioned over the hotspot after clicking.
    // emitting `mouseleave` forces the title to be removed
    // without relying on user to move the mouse back inside the tour.
    if (this.data.type === 'link') this.hotspotIcon.emit('mouseleave');
  },

  // event handlers end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // google analytics

  fireGoogleAnalyticsEvents(open) {
    if (!open) {
      switch (this.data.type) {
        case 'link':
          customGaEvent({
            action: GA_EVENTS.HOTSPOT_LINK_EXTERNAL,
            label: this.data.hotspotname,
          });
          break;
        case 'stop':
          customGaEvent({
            action: GA_EVENTS.HOTSPOT_LINK_STOP,
            label: this.data.hotspotname,
          });
          break;
        default:
          customGaEvent({
            action: `Hotspot (${this.data.type}) - Open`,
            label: this.data.hotspotname,
          });
          break;
      }
    }
  },

  // google analytics end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // content and collision handlers

  openAnimation() {
    if (this.disabled) {
      return null;
    }
    this.rotateToHotspot();

    if (this.hotspotContent) {
      this.scene.emit('contentopen');
      this.setCollisionClass(true);
      this.hotspotContent.setAttribute('visible', true);
      if (this.playButton) this.videoPlayButton();
      this.hotspotContent.emit('open');
      this.hotspotTitle && this.hotspotTitle.setAttribute('visible', true);
      this.translateTitle();
      this.updateIcon();
      this.translateContent();
    }
  },

  closeAnimation() {
    if (!this.underedits && this.hotspotContent) {
      this.hotspotContent.emit('close');
      this.scene.emit('contentclosed');
      this.setCollisionClass(false);
      setTimeout(() => {
        this.hotspotContent.setAttribute('visible', false);
        this.hotspotTitle && this.hotspotTitle.setAttribute('visible', false);
      }, 500);
      this.translateTitle();
      this.updateIcon();
      this.videoPlayButton();
    }
  },

  rotateToHotspot() {
    if (this.data.rotateto) {
      const camera = document.getElementById('camera');
      const [x, y, z] = this.data.rotateto.split(' ');
      camera.components['orbit-controls']?.rotateTo({ x, y, z });
    }
  },

  videoPlayButton() {
    if (this.data.active && this.playButton) {
      setTimeout(() => {
        this.playButton.setAttribute('visible', true);
        this.playButton.setAttribute('opacity', 0.7);
      }, 300);
    } else if (!this.data.active && this.playButton) {
      setTimeout(() => {
        this.playButton.setAttribute('visible', false);
        this.playButton.setAttribute('opacity', 0);
      }, 300);
    }
  },

  translateTitle(setVisible) {
    if (this.hotspotTitle && this.data.translatetitle) {
      this.data.active
        ? this.hotspotTitle.emit('open')
        : this.hotspotTitle.emit('close');
      if (setVisible) {
        this.hotspotTitle.setAttribute('visible', false);
      }
    }
  },

  translateContent() {
    if (this.hotspotContent) {
      const { x, y, z } = this.hotspotContent.getAttribute('position');
      let hotspotContentPosition = `${x} ${y} ${z}`;
      if (y !== this.hotspotContentYPosition) {
        const offsetY = this.iconSize / 2 || 0;
        this.hotspotContentYPosition = y + offsetY;
        hotspotContentPosition = `${x} ${this.hotspotContentYPosition} ${z}`;
      }
      this.hotspotContent.setAttribute('position', hotspotContentPosition);
    }
  },

  setCollisionClass(set) {
    if (set) {
      if (this.data.type !== 'video') {
        // video needs to handle class itself
        this.hotspotContent.setAttribute('class', 'hotspot-content');
      } else {
        this.hotspotContent.setAttribute('class', 'hotspot-content clickable');
      }
    } else {
      this.hotspotContent.setAttribute('class', '');
    }
  },

  setCollisionDetection(element, set) {
    const attributeMethod = set ? 'setAttribute' : 'removeAttribute';
    const listenerMethod = set ? 'addEventListener' : 'removeEventListener';
    element[attributeMethod]('aabb-collider', {
      objects: '.hotspot-content',
      ignoreId: this.data.content,
    });
    function removeAttributes(e) {
      element.setAttribute('visible', false);
      element.removeAttribute('class'); // need to remove class so the raycaster ignores it
    }

    function addAttributes(e) {
      element.setAttribute('visible', true);
      element.setAttribute('class', 'clickable');
    }

    element[listenerMethod]('hitstart', removeAttributes.bind(this));
    element[listenerMethod]('hitend', addAttributes.bind(this));
  },

  // animation, content and collision handlers end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // hotspot listeners

  setHotspotListeners(add) {
    const methodName = add ? 'addEventListener' : 'removeEventListener';

    if (!this.data.url) {
      this.el[methodName]('click', this.handleClick.bind(this));
      this.el[methodName]('keydown', this.handleKeyDown.bind(this));
    } else {
      this.el[methodName]('click', this.handleExternalLink.bind(this));
      this.el[methodName]('keydown', this.handleExternalLink.bind(this));
    }

    if (this.data.type !== 'highlight' || this.data.showlabel === true) {
      this.el[methodName](
        'mouseenter',
        this.handleMouseEnter.bind(this)
      );
      this.el[methodName](
        'mouseleave',
        this.handleMouseLeave.bind(this)
      );
    }
    this.el[methodName]('focus', this.handleFocus.bind(this));
    this.el[methodName]('focusout', this.handleFocusOut.bind(this));
    this.el[methodName](
      'rotateToHotspot',
      this.rotateToHotspot.bind(this)
    );
  },

  // hotspot listeners end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // icon animations

  setIconAnimations(set) {
    const methodName = set ? 'setAttribute' : 'removeAttrbute';
    this.hotspotIcon[methodName]('opacity', 0.9);
    this.hotspotIcon[methodName]('animation', {
      property: 'scale',
      elasticity: 100,
      to: '1.1 1.1 1.1',
      dir: 'alternate',
      loop: true,
      dur: 1000,
      pauseEvents: 'mouseenter, grow',
      resumeEvents: 'mouseleave, shrink',
    });
    this.hotspotIcon[methodName]('animation__opacity', {
      property: 'opacity',
      to: 1,
      dir: 'alternate',
      loop: true,
      dur: 1000,
      pauseEvents: 'mouseenter',
      resumeEvents: 'mouseleave',
    });
    this.hotspotIcon[methodName]('animation__opacity-hover', {
      property: 'opacity',
      to: 1,
      loop: false,
      dur: 200,
      startEvents: 'mouseenter',
    });
    this.hotspotIcon[methodName]('animation__grow-hover', {
      property: 'scale',
      to: '1.2 1.2 1.2',
      loop: false,
      dur: 200,
      startEvents: 'mouseenter, grow',
    });
    this.hotspotIcon[methodName]('animation__click', {
      property: 'scale',
      to: '1.1 1.1 1.1',
      dir: 'alternate',
      loop: 2,
      dur: 100,
      startEvents: 'click',
    });
  },

  removeIconAnimation(remove) {
    if (this.data.active || remove) {
      this.hotspotIcon.removeAttribute('animation');
      this.hotspotIcon.removeAttribute('animation__opacity');
      this.hotspotIcon.setAttribute('opacity', 1);
    }
  },

  updateIcon() {
    if (this.hotspotIcon) {
      const icon = this.data.darkmode
        ? '#hotspot__close-icon-black'
        : '#hotspot__close-icon';

      // Reset icon texture: icons of different sizes need a new texture map
      this.hotspotIcon.object3DMap.mesh.material.map = null;
      this.hotspotIcon.object3DMap.mesh.material.needsUpdate = true;
      this.data.active
        ? this.hotspotIcon.setAttribute('src', icon)
        : this.hotspotIcon.setAttribute('src', this.iconSrc);
    }
  },

  // icon listeners end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // content listeners

  setContentAnimations(set) {
    const methodName = set ? 'setAttribute' : 'removeAttribute';
    this.hotspotContent[methodName]('opacity', '0');
    this.hotspotContent[methodName]('animation__content-open-opacity', {
      property: 'opacity',
      from: 0,
      to: this.hotspotContentOpacity,
      dur: 500,
      startEvents: 'open',
    });
    this.hotspotContent[methodName]('animation__content-close-opacity', {
      property: 'opacity',
      to: 0,
      dur: 500,
      startEvents: 'close',
    });
  },

  // content listeners end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // title listeners

  initializeTitleAnimations(position) {
    if (position || this.data.translatetitle) {
      this.hotspotTitle.setAttribute('animation__title-open', {
        property: 'position',
        to: position || this.data.translatetitle,
        dur: 100,
        startEvents: 'open',
      });
      this.hotspotTitle.setAttribute('animation__title-close', {
        property: 'position',
        to: this.hotspotTitlePosition,
        dur: 100,
        delay: 500,
        startEvents: 'close',
      });
    }
  },

  removeTitleAnimations() {
    if (this.data.translatetitle) {
      this.hotspotTitle.removeAttribute('animation__title-open');
      this.hotspotTitle.removeAttribute('animation__title-close');
    }
  },

  // title listeners end
  //=======================================================================================

  //---------------------------------------------------------------------------------------
  // thumbnail listeners

  setThumbnailAnimations(set) {
    const attributeMethod = set ? 'setAttribute' : 'removeAttribute';
    const listenerMethod = set ? 'addEventListener' : 'removeEventListener';
    if (this.hotspotContent) {
      if (this.playButton) {
        this.hotspotContent[listenerMethod]('mouseenter', () =>
          this.toggleOpacity(this.playButton, 'enter')
        );
        this.hotspotContent[listenerMethod]('mouseleave', () =>
          this.toggleOpacity(this.playButton, 'leave')
        );
      }
      this.hotspotContent[attributeMethod]('animation__hover', {
        property: 'opacity',
        to: 1,
        from: 0.8,
        dur: 100,
        startEvents: 'mouseenter',
      });
      this.hotspotContent[attributeMethod]('animation__leave', {
        property: 'opacity',
        to: 0.8,
        from: 1,
        dur: 100,
        startEvents: 'mouseleave',
      });
    }
  },

  // thumbnail listeners end
  //=======================================================================================

  toggleOpacity(el, evt) {
    if (evt === 'enter') el.setAttribute('opacity', 1);
    if (evt === 'leave') el.setAttribute('opacity', 0.8);
  },

  reset(oldData) {
    if (oldData.active && !this.data.active) {
      this.closeAnimation();
      this.translateTitle(true);
    }
    if (!oldData.active && this.data.active) {
      this.openAnimation();
      this.translateTitle();
    }
  },
});
